/**
 * Empire fortifications — connectivity helpers + A* (no wall clipping).
 * Original names only.
 */

export const FORT_PZ = 1;
export const FORT_PX = 2;
export const FORT_NZ = 4;
export const FORT_NX = 8;

export const FORT_WALL_KINDS = new Set([
  "palisade",
  "wall",
  "fortified_wall",
]);

export const FORT_KINDS = new Set([...FORT_WALL_KINDS, "gate"]);

export type FortPoint = { x: number; z: number };

/** Long enough to ring a hill, not a 28-cell fence. */
export const FORT_LINE_MAX = 220;

export { terrainHeight, isCliffFace, bindTerrainView } from "./empire-terrain";

export function isFortKind(kind: string): boolean {
  return FORT_KINDS.has(kind);
}

export function isFortWallKind(kind: string): boolean {
  return FORT_WALL_KINDS.has(kind);
}

/**
 * Grid cells along any angle (not only cardinal / 45° / L).
 * Diagonal steps insert a cardinal neighbor so the wall stays 4-connected
 * and A* cannot slip through a corner touch.
 */
export function fortCellsAlong(
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  max = FORT_LINE_MAX
): FortPoint[] {
  let x = Math.round(x0);
  let z = Math.round(z0);
  const bx = Math.round(x1);
  const bz = Math.round(z1);
  const cells: FortPoint[] = [];
  const push = (px: number, pz: number) => {
    if (cells.length >= max) return;
    if (!cells.some((c) => c.x === px && c.z === pz)) cells.push({ x: px, z: pz });
  };
  push(x, z);
  if (x === bx && z === bz) return cells;

  const adx = Math.abs(bx - x);
  const adz = Math.abs(bz - z);
  const sx = x < bx ? 1 : -1;
  const sz = z < bz ? 1 : -1;
  let err = adx - adz;
  let guard = 0;
  while ((x !== bx || z !== bz) && cells.length < max && guard < max + 4) {
    guard += 1;
    const e2 = 2 * err;
    const px = x;
    const pz = z;
    if (e2 > -adz) {
      err -= adz;
      x += sx;
    }
    if (e2 < adx) {
      err += adx;
      z += sz;
    }
    if (x !== px && z !== pz) push(px, z);
    push(x, z);
  }
  return cells;
}

/** Stitch a clicked polyline into one cell list (hill-length walls). */
export function fortCellsAlongCorners(
  corners: FortPoint[],
  max = FORT_LINE_MAX
): FortPoint[] {
  if (!corners.length) return [];
  if (corners.length === 1) {
    return [{ x: Math.round(corners[0].x), z: Math.round(corners[0].z) }];
  }
  const out: FortPoint[] = [];
  for (let i = 0; i < corners.length - 1; i++) {
    const seg = fortCellsAlong(
      corners[i].x,
      corners[i].z,
      corners[i + 1].x,
      corners[i + 1].z,
      max - out.length + 1
    );
    for (const c of seg) {
      if (!out.some((o) => o.x === c.x && o.z === c.z)) out.push(c);
      if (out.length >= max) return out;
    }
  }
  return out;
}

export function fortNeighborMask(
  entities: Array<{ kind: string; x: number; z: number; hp: number }>,
  x: number,
  z: number
): number {
  const gx = Math.round(x);
  const gz = Math.round(z);
  const has = (dx: number, dz: number) =>
    entities.some(
      (e) =>
        e.hp > 0 &&
        isFortKind(e.kind) &&
        Math.round(e.x) === gx + dx &&
        Math.round(e.z) === gz + dz
    );
  let m = 0;
  if (has(0, 1)) m |= FORT_PZ;
  if (has(1, 0)) m |= FORT_PX;
  if (has(0, -1)) m |= FORT_NZ;
  if (has(-1, 0)) m |= FORT_NX;
  return m;
}

export function fortDegree(mask: number): number {
  let n = 0;
  let m = mask & 15;
  while (m) {
    n += m & 1;
    m >>= 1;
  }
  return n;
}

export function fortIsCorner(mask: number): boolean {
  if (fortDegree(mask) !== 2) return false;
  const opp =
    (mask & FORT_PZ && mask & FORT_NZ) || (mask & FORT_PX && mask & FORT_NX);
  return !opp;
}

export function fortStraightYaw(mask: number): number {
  if ((mask & FORT_PZ || mask & FORT_NZ) && !(mask & FORT_PX) && !(mask & FORT_NX)) {
    return Math.PI / 2;
  }
  return 0;
}

export function fortCornerYaw(mask: number): number {
  const pz = !!(mask & FORT_PZ);
  const px = !!(mask & FORT_PX);
  const nz = !!(mask & FORT_NZ);
  const nx = !!(mask & FORT_NX);
  if (px && pz) return 0;
  if (pz && nx) return Math.PI / 2;
  if (nx && nz) return Math.PI;
  if (nz && px) return -Math.PI / 2;
  return 0;
}

type PathNode = { x: number; z: number; g: number; f: number };

function heapPush(heap: PathNode[], node: PathNode) {
  heap.push(node);
  let i = heap.length - 1;
  while (i > 0) {
    const p = (i - 1) >> 1;
    if (heap[p].f <= heap[i].f) break;
    const tmp = heap[p];
    heap[p] = heap[i];
    heap[i] = tmp;
    i = p;
  }
}

function heapPop(heap: PathNode[]): PathNode | undefined {
  const top = heap[0];
  const last = heap.pop();
  if (!top || !last || heap.length === 0) return top;
  heap[0] = last;
  let i = 0;
  for (;;) {
    const l = i * 2 + 1;
    const r = l + 1;
    let s = i;
    if (l < heap.length && heap[l].f < heap[s].f) s = l;
    if (r < heap.length && heap[r].f < heap[s].f) s = r;
    if (s === i) break;
    const tmp = heap[s];
    heap[s] = heap[i];
    heap[i] = tmp;
    i = s;
  }
  return top;
}

/** Grid A* — walls block; caller decides gate walkability via `blocked`. */
export function findGridPath(
  sx: number,
  sz: number,
  gx: number,
  gz: number,
  blocked: (x: number, z: number) => boolean,
  mapHalf = 96,
  maxNodes = 2500,
  step = 4
): { path: FortPoint[]; reached: boolean } | null {
  const q = (v: number) => Math.round(v / step) * step;
  const startX = q(sx);
  const startZ = q(sz);
  const goalX = q(gx);
  const goalZ = q(gz);
  if (startX === goalX && startZ === goalZ) return { path: [{ x: gx, z: gz }], reached: true };

  const key = (x: number, z: number) => `${x},${z}`;
  const open: PathNode[] = [];
  const gScore = new Map<string, number>();
  const parent = new Map<string, string>();
  const closed = new Set<string>();
  const h = (x: number, z: number) => Math.abs(x - goalX) + Math.abs(z - goalZ);
  const sk = key(startX, startZ);
  const rebuild = (endKey: string): FortPoint[] => {
    const path: FortPoint[] = [];
    let k: string | undefined = endKey;
    while (k) {
      const [xs, zs] = k.split(",").map(Number);
      path.push({ x: xs, z: zs });
      if (k === sk) break;
      k = parent.get(k);
    }
    path.reverse();
    return path;
  };
  gScore.set(sk, 0);
  heapPush(open, { x: startX, z: startZ, g: 0, f: h(startX, startZ) });
  const dirs: Array<[number, number, number]> = [
    [step, 0, step],
    [-step, 0, step],
    [0, step, step],
    [0, -step, step],
    [step, step, step * 1.4],
    [step, -step, step * 1.4],
    [-step, step, step * 1.4],
    [-step, -step, step * 1.4],
  ];
  let expanded = 0;
  let bestKey = sk;
  let bestH = h(startX, startZ);
  while (open.length && expanded < maxNodes) {
    const cur = heapPop(open);
    if (!cur) break;
    const ck = key(cur.x, cur.z);
    if (closed.has(ck)) continue;
    if (cur.g !== (gScore.get(ck) ?? Infinity)) continue;
    closed.add(ck);
    expanded += 1;
    const ch = h(cur.x, cur.z);
    if (ch < bestH) {
      bestH = ch;
      bestKey = ck;
    }
    if (cur.x === goalX && cur.z === goalZ) {
      const path = rebuild(ck);
      const last = path[path.length - 1];
      if (!last || Math.hypot(last.x - gx, last.z - gz) > 0.2) path.push({ x: gx, z: gz });
      return { path, reached: true };
    }
    for (const [dx, dz, cost] of dirs) {
      const nx = cur.x + dx;
      const nz = cur.z + dz;
      if (Math.abs(nx) > mapHalf || Math.abs(nz) > mapHalf) continue;
      const nk = key(nx, nz);
      if (closed.has(nk)) continue;
      if ((nx !== goalX || nz !== goalZ) && blocked(nx, nz)) continue;
      if (dx !== 0 && dz !== 0 && (blocked(cur.x + dx, cur.z) || blocked(cur.x, cur.z + dz))) continue;
      const tg = cur.g + cost;
      if (tg >= (gScore.get(nk) ?? Infinity)) continue;
      parent.set(nk, ck);
      gScore.set(nk, tg);
      heapPush(open, { x: nx, z: nz, g: tg, f: tg + h(nx, nz) });
    }
  }
  if (bestKey === sk || bestH >= h(startX, startZ) - step) return null;
  return { path: rebuild(bestKey), reached: false };
}
